import { WORKFLOW_HEALTH, WORKFLOW_HEALTH_LABEL, type WorkflowHealth } from '@chatsdet/types';
import React from 'react';
import { Button, DateRangePicker, Dialog, DialogContent, DialogFooter, DialogTrigger, MoreFiltersButton, MultiSelectComboBox, type MultiSelectOption } from '@chatsdet/ui';
import { useEligibilityModel } from '../../../hooks/useEligibilityModel.js';
import type { FlowsSearchParams } from '../../../router.js';
import type { Tag } from '../../../hooks/useTags.js';
import type { WorkflowStatus } from '../../../hooks/useWorkflows.js';
import type { DateRangeValue } from '../../../utils/dateRange.js';
import {
  formatDateRangeDisplay,
  parseIsoToDateRange,
  toInclusiveIsoRange,
} from '../../../utils/dateRange.js';

export const STATUS_OPTIONS: MultiSelectOption[] = [
  { id: 'draft', label: 'Draft' },
  { id: 'approved', label: 'Approved' },
  { id: 'deleted', label: 'Deleted' },
];

/**
 * Suite-inclusion filter. Eligibility (approved + ever-passed) and pausing are
 * orthogonal, but they share one toolbar menu; each option maps to one of the
 * `eligible` / `paused` booleans on {@link FlowsSearchParams}.
 */
export const ELIGIBILITY_OPTIONS: MultiSelectOption[] = [
  { id: 'eligible', label: 'Eligible' },
  { id: 'not_eligible', label: 'Not eligible' },
  { id: 'paused', label: 'Paused' },
];

const ELIGIBILITY_LABELS: Record<string, string> = {
  eligible: 'Eligible',
  not_eligible: 'Not eligible',
  paused: 'Paused',
};

/** Legacy promotion filter (eligibility flag OFF). */
export const PROMOTION_OPTIONS: MultiSelectOption[] = [
  { id: 'promoted', label: 'Promoted' },
  { id: 'not_promoted', label: 'Not promoted' },
];

const PROMOTION_LABELS: Record<string, string> = {
  promoted: 'Promoted',
  not_promoted: 'Not promoted',
};

/** Derive the selected eligibility-menu option ids from the URL booleans. */
export function eligibilitySelectedIds(
  eligible: boolean | undefined,
  paused: boolean | undefined
): string[] {
  const ids: string[] = [];
  if (eligible === true) ids.push('eligible');
  else if (eligible === false) ids.push('not_eligible');
  if (paused === true) ids.push('paused');
  return ids;
}

/** Map a selected set of eligibility-menu option ids back to the URL booleans. */
export function eligibilityFromIds(ids: string[]): { eligible?: boolean; paused?: boolean } {
  const eligible = ids.includes('eligible')
    ? true
    : ids.includes('not_eligible')
      ? false
      : undefined;
  const paused = ids.includes('paused') ? true : undefined;
  return { eligible, paused };
}

export const LAST_RUN_STATUS_OPTIONS: MultiSelectOption[] = [
  { id: 'success', label: 'Passed' },
  { id: 'failed', label: 'Failed' },
  { id: 'running', label: 'Running' },
  { id: 'queued', label: 'Queued' },
  { id: 'canceled', label: 'Canceled' },
  { id: 'no_runs', label: 'No Runs' },
];

const isWorkflowHealth = (value: string): value is WorkflowHealth =>
  (WORKFLOW_HEALTH as readonly string[]).includes(value);

export const HEALTH_OPTIONS: MultiSelectOption[] = WORKFLOW_HEALTH.map((id) => ({
  id,
  label: WORKFLOW_HEALTH_LABEL[id],
}));

export const STATUS_LABELS: Record<WorkflowStatus, string> = {
  draft: 'Draft',
  approved: 'Approved',
  generating: 'Generating',
  building: 'Building',
  deleted: 'Deleted',
  diagnostic: 'Diagnostic',
};

export type AuthorOption = { id: string; label: string };

/** One removable filter tag in the lockup's secondary row. */
export type ActiveFilterChip = { id: string; label: string };

const LAST_RUN_STATUS_LABELS: Record<string, string> = Object.fromEntries(
  LAST_RUN_STATUS_OPTIONS.map((o) => [o.id, o.label])
);

type WorkflowFiltersProps = {
  statusFilter: string[];
  healthFilter: string[];
  /** When true, use the eligibility filter model; otherwise the promotion model. */
  eligibilityModel: boolean;
  eligibleFilter: boolean | undefined;
  pausedFilter: boolean | undefined;
  /** Promotion model (flag OFF) filter values. */
  promotionFilter: string[];
  lastRunStatusFilter: string[];
  tagFilter: string[];
  authorFilter: string[];
  favoritesFilter: boolean;
  dateRange: DateRangeValue;

  /** id → name for tag chip labels. */
  tagNameMap: Record<string, string>;
  authors: AuthorOption[];
  authorNameMap: Record<string, string>;
  authorsLoading: boolean;

  onFilterChange: (updates: Partial<FlowsSearchParams>) => void;
  onDateRangeChange: (range: DateRangeValue) => void;
  onClearDateRange: () => void;

  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

/**
 * The "More filters" dialog + reset/active-state helpers.
 *
 * The most-used filters (tags, status, health, last-run status, favorites,
 * search, sort) live inline in `FlowFilterBar` where they're always visible;
 * this dialog keeps the long-tail ones (created date range and author). The
 * trigger is a toolbar-style ghost button with a count badge for the
 * dialog-only filters.
 */
function WorkflowFilters({
  statusFilter,
  healthFilter,
  eligibilityModel,
  eligibleFilter,
  pausedFilter,
  promotionFilter,
  lastRunStatusFilter,
  tagFilter,
  authorFilter,
  favoritesFilter,
  dateRange,
  tagNameMap,
  authors,
  authorNameMap,
  authorsLoading,
  onFilterChange,
  onDateRangeChange,
  onClearDateRange,
  isOpen,
  onOpenChange,
}: WorkflowFiltersProps) {
  const safeLastRunStatusFilter = lastRunStatusFilter ?? [];

  const dateActive = Boolean(formatDateRangeDisplay(dateRange));

  // Every active filter as a removable tag — rendered in the lockup's
  // secondary row by FlowFilterBar.
  const activeFilterChips = React.useMemo<ActiveFilterChip[]>(() => {
    const chips: ActiveFilterChip[] = [];

    if (favoritesFilter) {
      chips.push({ id: 'favorites', label: 'Favorites' });
    }
    statusFilter.forEach((status) => {
      chips.push({
        id: `status:${status}`,
        label: `Status: ${STATUS_LABELS[status as WorkflowStatus] ?? status}`,
      });
    });
    healthFilter.forEach((health) => {
      chips.push({ id: `health:${health}`, label: `Health: ${isWorkflowHealth(health) ? WORKFLOW_HEALTH_LABEL[health] : health}` });
    });
    if (eligibilityModel) {
      eligibilitySelectedIds(eligibleFilter, pausedFilter).forEach((id) => {
        chips.push({
          id: `eligibility:${id}`,
          label: `Eligibility: ${ELIGIBILITY_LABELS[id] ?? id}`,
        });
      });
    } else {
      promotionFilter.forEach((promotion) => {
        chips.push({
          id: `promotion:${promotion}`,
          label: `Promotion: ${PROMOTION_LABELS[promotion] ?? promotion}`,
        });
      });
    }
    safeLastRunStatusFilter.forEach((runStatus) => {
      chips.push({
        id: `lastRunStatus:${runStatus}`,
        label: `Last run: ${LAST_RUN_STATUS_LABELS[runStatus] ?? runStatus}`,
      });
    });
    tagFilter.forEach((tagId) => {
      chips.push({ id: `tag:${tagId}`, label: `Tag: ${tagNameMap[tagId] ?? tagId}` });
    });
    authorFilter.forEach((userId) => {
      chips.push({ id: `author:${userId}`, label: `Author: ${authorNameMap[userId] ?? userId}` });
    });
    const dateLabel = formatDateRangeDisplay(dateRange);
    if (dateLabel) {
      chips.push({ id: 'dateRange', label: `Created: ${dateLabel}` });
    }

    return chips;
  }, [favoritesFilter, statusFilter, healthFilter, eligibilityModel, eligibleFilter, pausedFilter, promotionFilter, safeLastRunStatusFilter, tagFilter, authorFilter, tagNameMap, authorNameMap, dateRange]);

  const handleFilterRemove = React.useCallback(
    (chipId: string) => {
      if (chipId === 'favorites') {
        onFilterChange({ favorites: undefined, page: 1 });
      } else if (chipId === 'dateRange') {
        onClearDateRange();
      } else if (chipId.startsWith('status:')) {
        const status = chipId.split(':', 2)[1];
        const next = statusFilter.filter((s) => s !== status);
        onFilterChange({ status: next.length > 0 ? next : undefined, page: 1 });
      } else if (chipId.startsWith('health:')) {
        const health = chipId.split(':', 2)[1];
        const next = healthFilter.filter((h) => h !== health);
        onFilterChange({ health: next.length > 0 ? next : undefined, page: 1 });
      } else if (chipId.startsWith('eligibility:')) {
        const option = chipId.split(':', 2)[1];
        // `eligible`/`not_eligible` share the `eligible` boolean; `paused` is its own.
        onFilterChange(
          option === 'paused' ? { paused: undefined, page: 1 } : { eligible: undefined, page: 1 }
        );
      } else if (chipId.startsWith('promotion:')) {
        const promotion = chipId.split(':', 2)[1];
        const next = promotionFilter.filter((p) => p !== promotion);
        onFilterChange({ promotion: next.length > 0 ? next : undefined, page: 1 });
      } else if (chipId.startsWith('lastRunStatus:')) {
        const runStatus = chipId.split(':', 2)[1];
        const next = safeLastRunStatusFilter.filter((s) => s !== runStatus);
        onFilterChange({ lastRunStatus: next.length > 0 ? next : undefined, page: 1 });
      } else if (chipId.startsWith('tag:')) {
        const tagId = chipId.split(':', 2)[1];
        const next = tagFilter.filter((id) => id !== tagId);
        onFilterChange({ tagIds: next.length > 0 ? next : undefined, page: 1 });
      } else if (chipId.startsWith('author:')) {
        const userId = chipId.split(':', 2)[1];
        const next = authorFilter.filter((id) => id !== userId);
        onFilterChange({ createdByIds: next.length > 0 ? next : undefined, page: 1 });
      }
    },
    [statusFilter, healthFilter, promotionFilter, safeLastRunStatusFilter, tagFilter, authorFilter, onClearDateRange, onFilterChange]
  );

  const filtersCount = activeFilterChips.length;
  const hasActiveFilters = filtersCount > 0;

  // Count of active DIALOG-ONLY filters, shown on the "More filters" trigger
  const modalFiltersCount = authorFilter.length + (dateActive ? 1 : 0);

  const handleResetFilters = React.useCallback(() => {
    onFilterChange({
      status: undefined,
      health: undefined,
      promotion: undefined,
      eligible: undefined,
      paused: undefined,
      lastRunStatus: undefined,
      tagIds: undefined,
      createdByIds: undefined,
      favorites: undefined,
      page: 1,
    });
    onClearDateRange();
  }, [onClearDateRange, onFilterChange]);

  const authorOptions: MultiSelectOption[] = React.useMemo(
    () => authors.map((a) => ({ id: a.id, label: a.label })),
    [authors]
  );

  const selectedAuthors: MultiSelectOption[] = React.useMemo(
    () =>
      authorFilter.map((id) => ({
        id,
        label: authorNameMap[id] ?? id,
      })),
    [authorFilter, authorNameMap]
  );

  const handleAuthorChange = React.useCallback(
    (items: MultiSelectOption[]) => {
      onFilterChange({
        createdByIds: items.length > 0 ? items.map((i) => i.id) : undefined,
        page: 1,
      });
    },
    [onFilterChange]
  );

  const filterDialogTrigger = (
    <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange} size="lg">
      <MoreFiltersButton count={modalFiltersCount} />
      <Dialog
        title="More filters"
        description="Narrow down workflows by creation date or author."
      >
        <DialogContent className="grid gap-4 md:grid-cols-2">
          <DateRangePicker
            label="Created"
            value={dateRange}
            onChange={onDateRangeChange}
            granularity="day"
            className="md:col-span-2"
          />
          <MultiSelectComboBox
            label="Author"
            placeholder="Search authors..."
            items={authorOptions}
            value={selectedAuthors}
            onChange={handleAuthorChange}
            isLoading={authorsLoading}
            className="md:col-span-2"
          />
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" onPress={handleResetFilters}>
            Reset filters
          </Button>
          <Button variant="primary" onPress={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );

  return {
    filterDialogTrigger,
    activeFilterChips,
    handleFilterRemove,
    hasActiveFilters,
    filtersCount,
    handleResetFilters,
  };
}

/**
 * Hook to manage workflow filters with URL state
 */
export function useWorkflowFilters(params: {
  searchParams: FlowsSearchParams;
  onFilterChange: (updates: Partial<FlowsSearchParams>) => void;
  /** Used for tag chip labels; the tag picker itself lives inline in FlowFilterBar. */
  tags: Tag[];
  authors?: AuthorOption[];
  authorsLoading?: boolean;
}) {
  const {
    searchParams,
    onFilterChange,
    tags,
    authors = [],
    authorsLoading = false,
  } = params;

  const [isOpen, setIsOpen] = React.useState(false);
  const eligibilityModel = useEligibilityModel();

  const tagNameMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    for (const tag of tags) {
      map[tag.id] = tag.name;
    }
    return map;
  }, [tags]);

  const authorNameMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    for (const author of authors) {
      map[author.id] = author.label;
    }
    return map;
  }, [authors]);

  // Date range state from URL params - use parseIsoToDateRange to convert ISO strings to DateValue
  const dateRange: DateRangeValue = React.useMemo(
    () => parseIsoToDateRange(searchParams.dateFrom, searchParams.dateTo),
    [searchParams.dateFrom, searchParams.dateTo]
  );

  const handleDateRangeChange = React.useCallback(
    (range: DateRangeValue) => {
      const { start, end } = toInclusiveIsoRange(range);
      onFilterChange({
        dateFrom: start ? start.split('T')[0] : undefined,
        dateTo: end ? end.split('T')[0] : undefined,
        page: 1,
      });
    },
    [onFilterChange]
  );

  const handleClearDateRange = React.useCallback(() => {
    onFilterChange({
      dateFrom: undefined,
      dateTo: undefined,
      page: 1,
    });
  }, [onFilterChange]);

  const filtersResult = WorkflowFilters({
    statusFilter: searchParams.status ?? [],
    healthFilter: searchParams.health ?? [],
    eligibilityModel,
    eligibleFilter: searchParams.eligible,
    pausedFilter: searchParams.paused,
    promotionFilter: searchParams.promotion ?? [],
    lastRunStatusFilter: searchParams.lastRunStatus ?? [],
    tagFilter: searchParams.tagIds ?? [],
    authorFilter: searchParams.createdByIds ?? [],
    favoritesFilter: searchParams.favorites === true,
    dateRange,
    tagNameMap,
    authors,
    authorNameMap,
    authorsLoading,
    onFilterChange,
    onDateRangeChange: handleDateRangeChange,
    onClearDateRange: handleClearDateRange,
    isOpen,
    onOpenChange: setIsOpen,
  });

  return filtersResult;
}
